import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { brandColors, journeyTabTints } from '@config/brandTokens';
import { agePillButtonSx } from '@components/contest/contestChromeShared';

export type ContestJourneyTab =
  | 'benchmark'
  | 'sports_iq'
  | 'learn'
  | 'self_evaluation'
  | 'contest'
  | 'leaderboard';

export const CONTEST_JOURNEY_TABS: {
  id: ContestJourneyTab;
  label: string;
  activeColor: string;
  tint: (typeof journeyTabTints)[keyof typeof journeyTabTints];
}[] = [
  { id: 'benchmark', label: 'Benchmark', activeColor: '#0D9488', tint: journeyTabTints.benchmark },
  { id: 'sports_iq', label: 'Sports IQ', activeColor: '#CA8A04', tint: journeyTabTints.sports_iq },
  { id: 'learn', label: 'Learn', activeColor: brandColors.blue, tint: journeyTabTints.learn },
  { id: 'self_evaluation', label: 'Self Evaluation', activeColor: brandColors.violet, tint: journeyTabTints.self_evaluation },
  { id: 'contest', label: 'Contest', activeColor: brandColors.orange, tint: journeyTabTints.contest },
  { id: 'leaderboard', label: 'Leaderboard', activeColor: brandColors.green, tint: journeyTabTints.leaderboard },
];

const TAB_SKEW = '-12deg';
const CONTENT_BG = brandColors.ice;
const TAB_MIN_WIDTH = { xs: 92, sm: 118, md: 132 };

/** Numbered journey steps only — Benchmark and Sports IQ stay unnumbered. */
const JOURNEY_STEP_NUMBERS: Partial<Record<ContestJourneyTab, 1 | 2 | 3 | 4>> = {
  learn: 1,
  self_evaluation: 2,
  contest: 3,
  leaderboard: 4,
};

export function contestTabFromQuery(value: string | null): ContestJourneyTab {
  if (value === 'benchmark') return 'benchmark';
  if (value === 'sports-iq' || value === 'sports_iq') return 'sports_iq';
  if (value === 'self-evaluation' || value === 'self_evaluation') return 'self_evaluation';
  if (value === 'contest') return 'contest';
  if (value === 'leaderboard') return 'leaderboard';
  return 'learn';
}

export function contestTabToQuery(tab: ContestJourneyTab): string | null {
  if (tab === 'benchmark') return 'benchmark';
  if (tab === 'sports_iq') return 'sports-iq';
  if (tab === 'self_evaluation') return 'self-evaluation';
  if (tab === 'contest') return 'contest';
  if (tab === 'leaderboard') return 'leaderboard';
  return null;
}

interface ContestSlantedTabsProps {
  value: ContestJourneyTab;
  onChange: (tab: ContestJourneyTab) => void;
}

/** Slanted folder-style tabs — inactive tinted; active solid accent + white label. */
export const ContestSlantedTabs: React.FC<ContestSlantedTabsProps> = ({ value, onChange }) => (
  <Box
    role="tablist"
    aria-label="Contest journey"
    sx={{
      display: 'flex',
      alignItems: 'flex-end',
      gap: { xs: 0.75, sm: 1.1, md: 1.5 },
      width: '100%',
      maxWidth: { xs: '100%', lg: 1120 },
      minWidth: { md: 640 },
      flex: { lg: 1 },
      position: 'relative',
      pb: 0,
      overflowX: { xs: 'auto', md: 'visible' },
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: '1px',
        bgcolor: '#cbd5e1',
        zIndex: 0,
      },
    }}
  >
    {CONTEST_JOURNEY_TABS.map((tab) => {
      const active = value === tab.id;
      const stepNumber = JOURNEY_STEP_NUMBERS[tab.id];
      return (
        <Box
          key={tab.id}
          sx={{
            position: 'relative',
            zIndex: active ? 2 : 1,
            flex: '1 1 0',
            minWidth: { xs: 96, sm: 0 },
          }}
        >
          <Box
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange(tab.id);
              }
            }}
            sx={{
              transform: `skewX(${TAB_SKEW})`,
              cursor: 'pointer',
              userSelect: 'none',
              minHeight: active ? 50 : 40,
              minWidth: TAB_MIN_WIDTH,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 1, sm: 1.25, md: 2 },
              pt: active ? 1.2 : 0.85,
              pb: active ? 1.3 : 0.7,
              background: active
                ? tab.activeColor
                : `linear-gradient(180deg, ${tab.tint.bg} 0%, ${tab.tint.rail} 100%)`,
              color: active ? brandColors.white : brandColors.slate,
              border: `1px solid ${active ? tab.activeColor : tab.tint.border}`,
              borderBottom: active ? `1px solid ${CONTENT_BG}` : `1px solid ${tab.tint.border}`,
              borderTop: active ? `3px solid ${tab.activeColor}` : `2px solid ${tab.tint.border}`,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              boxShadow: active
                ? `0 -1px 0 rgba(255,255,255,0.25) inset, 0 2px 8px ${tab.activeColor}44`
                : 'inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -1px 0 rgba(17,23,57,0.04)',
              transition: 'min-height 0.15s ease, border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
              '&:hover': {
                color: active ? brandColors.white : tab.activeColor,
                borderColor: tab.activeColor,
                background: active
                  ? tab.activeColor
                  : `linear-gradient(180deg, ${tab.tint.bg} 0%, #fff 100%)`,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 0.4, sm: 0.5 },
                transform: 'skewX(12deg)',
                minWidth: 0,
              }}
            >
              {stepNumber != null && (
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    width: { xs: 16, md: 17 },
                    height: { xs: 16, md: 17 },
                    flexShrink: 0,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: '"Sora", Arial, sans-serif',
                    fontWeight: 800,
                    fontSize: { xs: '0.62rem', sm: '0.65rem', md: '0.68rem' },
                    lineHeight: 1,
                    letterSpacing: 0,
                    ...(active
                      ? {
                          bgcolor: 'rgba(255,255,255,0.2)',
                          color: brandColors.white,
                          border: '1px solid rgba(255,255,255,0.42)',
                        }
                      : {
                          bgcolor: '#fff',
                          color: `${tab.activeColor}bb`,
                          border: `1px solid ${tab.tint.border}`,
                        }),
                  }}
                >
                  {stepNumber}
                </Box>
              )}
              <Typography
                component="span"
                sx={{
                  fontWeight: active ? 800 : 600,
                  fontSize: { xs: '0.72rem', sm: '0.8rem', md: '0.9rem' },
                  fontFamily: '"Sora", Arial, sans-serif',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  letterSpacing: active ? 0.2 : 0,
                }}
              >
                {tab.label}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    })}
  </Box>
);

export interface ContestJourneyHeaderProps {
  sportName: string;
  sportIcon?: React.ReactNode;
  activeTab: ContestJourneyTab;
  onTabChange: (tab: ContestJourneyTab) => void;
  ageCategory: string;
  categories: readonly string[];
  onAgeChange: (age: string) => void;
  /** Learn tab — highlight multiple ages when comparing benchmarks */
  compareMode?: boolean;
  compareAges?: string[];
  /** Shown before age pills (e.g. Gender Boy/Girl) */
  ageLeading?: React.ReactNode;
  /** Shown before gender — profile / child picker */
  profileSlot?: React.ReactNode;
  ageTrailing?: React.ReactNode;
  belowAge?: React.ReactNode;
  /** Contest tab: show the profile age only — no other category pills. */
  ageLocked?: boolean;
}

/** Sport title + tab strip on one row; gender + age pills on the content strip. */
export const ContestJourneyHeader: React.FC<ContestJourneyHeaderProps> = ({
  sportName,
  sportIcon,
  activeTab,
  onTabChange,
  ageCategory,
  categories,
  onAgeChange,
  compareMode = false,
  compareAges = [],
  ageLeading,
  profileSlot,
  ageTrailing,
  belowAge,
  ageLocked = false,
}) => {
  const activeTabMeta = CONTEST_JOURNEY_TABS.find((t) => t.id === activeTab);
  const stripAccent = activeTabMeta?.activeColor ?? brandColors.blue;

  const fieldLabelSx = {
    fontWeight: 800,
    fontSize: '0.72rem',
    letterSpacing: 0.6,
    textTransform: 'uppercase' as const,
    color: brandColors.slate,
    fontFamily: '"Sora", Arial, sans-serif',
    whiteSpace: 'nowrap' as const,
  };

  return (
    <Box
      sx={{
        bgcolor: brandColors.white,
        borderRadius: 2.5,
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 4px rgba(17,23,57,0.05)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: { xs: 1.25, md: 1.75 },
          pt: 1.25,
          pb: 0,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { xs: 'stretch', lg: 'flex-end' },
          gap: { xs: 1.25, lg: 2 },
          bgcolor: brandColors.white,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
            minWidth: 0,
            pb: { xs: 0, lg: 0.85 },
          }}
        >
          {sportIcon && (
            <Box sx={{ color: brandColors.navy, display: 'flex', '& .MuiSvgIcon-root': { fontSize: 26 } }}>
              {sportIcon}
            </Box>
          )}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              color: brandColors.navy,
              fontFamily: '"Sora", Arial, sans-serif',
              whiteSpace: 'nowrap',
            }}
          >
            {sportName} Contest
          </Typography>
        </Box>
        <ContestSlantedTabs value={activeTab} onChange={onTabChange} />
      </Box>

      <Box
        sx={{
          px: { xs: 1.25, md: 1.75 },
          py: 1.25,
          bgcolor: CONTENT_BG,
          borderTop: `2px solid ${stripAccent}`,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          flexWrap: 'wrap',
          alignItems: { xs: 'stretch', lg: 'center' },
          gap: { xs: 1.25, lg: 2 },
        }}
      >
        {profileSlot ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            <Typography component="span" sx={fieldLabelSx}>
              Profile
            </Typography>
            {profileSlot}
          </Box>
        ) : null}
        {ageLeading ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            <Typography component="span" sx={fieldLabelSx}>
              Gender
            </Typography>
            {ageLeading}
          </Box>
        ) : null}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
          <Typography component="span" sx={fieldLabelSx}>
            Age Category
          </Typography>
          {ageLocked ? (
            <Chip
              size="small"
              label={ageCategory || 'Add date of birth'}
              sx={{
                fontWeight: 800,
                bgcolor: ageCategory ? brandColors.white : '#fff7ed',
                color: ageCategory ? brandColors.navy : '#c2410c',
                border: `1px solid ${ageCategory ? `${brandColors.navy}44` : '#fdba74'}`,
              }}
            />
          ) : (
            categories.map((age) => {
              const selected = compareMode ? compareAges.includes(age) : ageCategory === age;
              return (
                <Button key={age} size="small" onClick={() => onAgeChange(age)} sx={agePillButtonSx(selected)}>
                  {age}
                </Button>
              );
            })
          )}
        </Box>
        {ageTrailing}
      </Box>
      {belowAge}
    </Box>
  );
};
